import React from "react";

interface IvaScoreDisplayProps {
  scores: Record<string, any>;
  interpretations: Record<string, any>;
}

const IvaScoreDisplay: React.FC<IvaScoreDisplayProps> = ({ 
  scores, 
  interpretations 
}) => {
  // Define the score categories for IVA
  const categories = [
    {
      title: "Valoración de la Situación",
      description: "Cómo se percibe o interpreta la situación estresante",
      scores: ["vsa", "vsd", "vsi"]
    },
    {
      title: "Afrontamiento Cognitivo",
      description: "Estrategias mentales para manejar la situación",
      scores: ["acs", "ace"]
    },
    {
      title: "Afrontamiento Conductual Motor",
      description: "Acciones concretas para manejar la situación",
      scores: ["acms", "acme"]
    },
    {
      title: "Estrategias de Evitación",
      description: "Formas de evitar enfrentar la situación",
      scores: ["ap", "ae"]
    }
  ];

  // Helper function to get color based on IVA scores and category
  const getScoreColor = (key: string, percentage: number): string => {
    // Different scoring logic based on the type of scale
    if (key === "vsd" || key === "acs" || key === "ace" || key === "acms" || key === "acme") {
      // These are adaptive strategies, so higher is better
      if (percentage < 40) return "text-red-500";
      if (percentage < 70) return "text-yellow-500";
      return "text-green-500";
    } else {
      // These are maladaptive strategies (vsa, vsi, ap, ae), so lower is better
      if (percentage > 70) return "text-red-500";
      if (percentage > 40) return "text-yellow-500";
      return "text-green-500";
    }
  };

  // Helper function to get background color based on IVA scores and category
  const getBarColor = (key: string, percentage: number): string => {
    // Different scoring logic based on the type of scale
    if (key === "vsd" || key === "acs" || key === "ace" || key === "acms" || key === "acme") {
      // These are adaptive strategies, so higher is better
      if (percentage < 40) return "bg-red-500";
      if (percentage < 70) return "bg-yellow-500";
      return "bg-green-500";
    } else {
      // These are maladaptive strategies (vsa, vsi, ap, ae), so lower is better
      if (percentage > 70) return "bg-red-500";
      if (percentage > 40) return "bg-yellow-500";
      return "bg-green-500";
    }
  };

  // Helper function to get interpretation text
  const getInterpretationText = (key: string, percentage: number): string => {
    // If we have an interpretation from the API, use that
    if (interpretations && interpretations[key]) {
      return interpretations[key];
    }
    
    // Otherwise generate a default interpretation based on score and scale type
    if (key === "vsd" || key === "acs" || key === "ace" || key === "acms" || key === "acme") {
      // These are adaptive strategies, so higher is better
      if (percentage < 40) return "Bajo";
      if (percentage < 70) return "Moderado";
      return "Alto";
    } else {
      // These are maladaptive strategies (vsa, vsi, ap, ae), so lower is better
      if (percentage > 70) return "Alto";
      if (percentage > 40) return "Moderado";
      return "Bajo";
    }
  };
  
  // Debug log
  console.log("IVA scores:", scores);
  console.log("IVA interpretations:", interpretations);

  return (
    <div className="space-y-8">
      {categories.map((category, categoryIndex) => (
        <div key={categoryIndex} className="mb-6">
          <h5 className="text-lg font-bold text-navy-700 dark:text-white mb-2">
            {category.title}
          </h5>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            {category.description}
          </p>

          <div className="space-y-4">
            {category.scores.map((key) => {
              const score = scores[key];
              if (!score) return null;
              
              const percentage = score.percentage || (score.raw_score / score.max_score) * 100;
              const interpretationText = getInterpretationText(key, percentage);
              const textColor = getScoreColor(key, percentage);
              const barColor = getBarColor(key, percentage);
              
              return (
                <div key={key} className="w-full">
                  <div className="flex flex-wrap justify-between items-center mb-1">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {score.name}
                    </span>
                    <div className="flex items-center">
                      <span className="text-xs text-gray-500 dark:text-gray-400 mr-2">
                        {percentage.toFixed(1)}%
                      </span>
                      <span className={`text-sm font-medium ${textColor}`}>
                        {interpretationText}
                      </span>
                    </div>
                  </div>
                  
                  <div className="w-full bg-gray-200 dark:bg-navy-700 rounded-full h-2.5">
                    <div 
                      className={`h-2.5 rounded-full ${barColor}`} 
                      style={{ width: `${Math.max(5, Math.min(100, percentage))}%` }}
                    ></div>
                  </div>
                  
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {score.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Legend */}
      <div className="mt-8 pt-4 border-t border-gray-200 dark:border-gray-700">
        <h5 className="text-md font-bold text-navy-700 dark:text-white mb-3">
          Leyenda
        </h5>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h6 className="text-sm font-semibold mb-2">Estrategias Adaptativas</h6>
            <p className="text-xs mb-2">VSD, ACS, ACE, ACMS, ACME</p>
            <div className="space-y-2">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                <span className="text-xs">Alto nivel (≥ 70%): Uso frecuente de estrategias adaptativas</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-yellow-500 mr-2"></div>
                <span className="text-xs">Nivel moderado (40-69%): Uso moderado de estrategias adaptativas</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                <span className="text-xs">Bajo nivel (&lt; 40%): Uso limitado de estrategias adaptativas</span>
              </div>
            </div>
          </div>
          <div>
            <h6 className="text-sm font-semibold mb-2">Estrategias Menos Adaptativas</h6>
            <p className="text-xs mb-2">VSA, VSI, AP, AE</p>
            <div className="space-y-2">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                <span className="text-xs">Bajo nivel (&lt; 40%): Uso limitado de estrategias menos adaptativas</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-yellow-500 mr-2"></div>
                <span className="text-xs">Nivel moderado (40-69%): Uso moderado de estrategias menos adaptativas</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                <span className="text-xs">Alto nivel (≥ 70%): Uso frecuente de estrategias menos adaptativas</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IvaScoreDisplay; 